'use client'

import React, { useState } from 'react'
import { ChevronLeft, ChevronRight } from 'lucide-react'
import { cn } from '@/lib/utils/cn'
import { AppErrorBoundary } from '@/components/common/AppErrorBoundary'

export interface WizardStep {
  id: number
  title: string
  description?: string
  content: React.ReactNode
  isComplete: boolean
}

interface FullscreenWizardProps {
  steps: WizardStep[]
  currentStep: number
  onStepChange: (step: number) => void
  onComplete: () => void
  isSubmitting?: boolean
  submitLabel?: string
  header?: React.ReactNode
}

export function FullscreenWizard({
  steps,
  currentStep,
  onStepChange,
  onComplete,
  isSubmitting = false,
  submitLabel = 'Complete',
  header
}: FullscreenWizardProps) {
  const [touchStart, setTouchStart] = useState<number | null>(null)
  const [touchEnd, setTouchEnd] = useState<number | null>(null)

  const minSwipeDistance = 50

  const onTouchStart = (e: React.TouchEvent) => {
    setTouchEnd(null)
    const touch = e.targetTouches[0]
    if (touch) {
      setTouchStart(touch.clientX)
    }
  }

  const onTouchMove = (e: React.TouchEvent) => {
    const touch = e.targetTouches[0]
    if (touch) {
      setTouchEnd(touch.clientX)
    }
  }

  const onTouchEnd = () => {
    if (!touchStart || !touchEnd) return
    const distance = touchStart - touchEnd
    const isLeftSwipe = distance > minSwipeDistance
    const isRightSwipe = distance < -minSwipeDistance

    if (isLeftSwipe && currentStep < steps.length - 1) {
      // Only advance if current step is complete
      const currentStepData = steps[currentStep]
      if (currentStepData?.isComplete) {
        onStepChange(currentStep + 1)
      }
    }
    if (isRightSwipe && currentStep > 0) {
      onStepChange(currentStep - 1)
    }
  }

  const handleStepDotClick = (index: number) => {
    const step = steps[index]
    // Allow navigation to completed steps or steps we've already visited
    if (step && (step.isComplete || index <= currentStep)) {
      onStepChange(index)
    }
  }

  return (
    <AppErrorBoundary source="fullscreen-wizard">
      <div className="flex flex-col max-lg:min-h-screen">
        {/* Header */}
        <div className="flex-shrink-0 p-4 border-b border-theme-border-primary bg-theme-bg-secondary">
          {header && <div className="mb-4">{header}</div>}

          {/* Progress dots */}
          <div className="flex justify-center gap-2">
            {steps.map((step, index) => (
              <button
                key={step.id}
                onClick={() => handleStepDotClick(index)}
                disabled={!step.isComplete && index > currentStep}
                className={cn(
                  'w-8 h-8 rounded-full text-sm font-medium transition-all',
                  index === currentStep
                    ? 'accent-bg-primary text-[var(--color-text-on-accent)]'
                    : step.isComplete
                      ? 'bg-theme-bg-tertiary text-theme-text-primary cursor-pointer hover:accent-bg-primary/50'
                      : 'bg-theme-bg-tertiary text-theme-text-muted cursor-not-allowed opacity-50'
                )}
                aria-label={`Go to step ${index + 1}`}
              >
                {index + 1}
              </button>
            ))}
          </div>
        </div>

        {/* Card container - sizes to content on desktop, fills space on mobile */}
        <div className="max-lg:flex-1 overflow-hidden relative bg-theme-bg-secondary">
          <div
            className="flex transition-transform duration-300 ease-out max-lg:h-full"
            style={{ transform: `translateX(-${currentStep * 100}%)` }}
            onTouchStart={onTouchStart}
            onTouchMove={onTouchMove}
            onTouchEnd={onTouchEnd}
          >
            {steps.map((step, index) => (
              <div
                key={step.id}
                className="w-full flex-shrink-0 overflow-y-auto p-6 lg:p-8 lg:overflow-y-visible"
              >
                <h2 className="text-2xl font-bold text-theme-text-primary mb-2">
                  {step.title}
                </h2>
                {step.description && (
                  <p className="text-theme-text-secondary mb-6">{step.description}</p>
                )}
                {step.content}
              </div>
            ))}
          </div>
        </div>

        {/* Footer - sticky navigation */}
        <div className="flex-shrink-0 p-4 border-t border-theme-border-primary bg-theme-bg-secondary">
          <div className="flex justify-between items-center">
            <button
              onClick={() => onStepChange(currentStep - 1)}
              disabled={currentStep === 0}
              className={cn(
                'flex items-center gap-2 px-4 py-2 rounded-lg transition-all',
                currentStep === 0
                  ? 'opacity-0 pointer-events-none'
                  : 'bg-theme-bg-tertiary text-theme-text-primary hover:bg-theme-bg-elevated'
              )}
              aria-label="Go to previous step"
            >
              <ChevronLeft className="w-5 h-5" />
              Back
            </button>

            {currentStep === steps.length - 1 ? (
              <button
                onClick={onComplete}
                disabled={!steps[currentStep]?.isComplete || isSubmitting}
                className="flex items-center gap-2 px-6 py-2 rounded-lg accent-bg-primary text-[var(--color-text-on-accent)] hover:accent-bg-secondary disabled:opacity-50 disabled:cursor-not-allowed"
                aria-label={submitLabel}
              >
                {isSubmitting ? 'Creating...' : submitLabel}
              </button>
            ) : (
              <button
                onClick={() => onStepChange(currentStep + 1)}
                disabled={!steps[currentStep]?.isComplete}
                className="flex items-center gap-2 px-6 py-2 rounded-lg accent-bg-primary text-[var(--color-text-on-accent)] hover:accent-bg-secondary disabled:opacity-50 disabled:cursor-not-allowed"
                aria-label="Go to next step"
              >
                Next
                <ChevronRight className="w-5 h-5" />
              </button>
            )}
          </div>
        </div>
      </div>
    </AppErrorBoundary>
  )
}

